import { ElementCategory, ElementChangedFlags, NodeUserData, SceneElement } from "../core/mdl";
import { PropertyChangedResult } from "../core/prop";
import { SetPropertyValueOptions } from "../eaiws/basket";
import { Lock } from "../utils/async";
import { ERR_INVALID_STATE, newError, rejectError } from "../utils/error";
import { Interactors } from "./oap/Interactors";
import { ArticlePropertyHelpers } from "./ArticlePropertyHelpers";
import { ARTICLEELEMENT_NODEDATA_BASKETID } from "./Constants";
export class ArticleElement extends SceneElement {
    /**
     * BasketId may be null if the article is not intantiated yes (use ensureIsInstanced()).
     * e.g. if article is a newly created clone
     */
    get basketId() {
        return (this.mBasketId);
    }
    get category() {
        return (ElementCategory.Interior);
    }
    /**
     * Returns false if the article (including its children) has no or an empty geometry.
     */
    get hasGeometry() {
        return (this.boundingBox.isValid());
    }
    constructor(pBasketId, pManager, pNode) {
        super(pManager.app.model, pNode);
        this.onSetPropertyValue = (pEaiProperty, pValue) => {
            if (pEaiProperty == null)
                return (Promise.resolve(PropertyChangedResult.Nothing));
            let tStrValue = null;
            if (pValue != null)
                tStrValue = pValue.toString();
            return (this.setEaiwsPropertyValue(pEaiProperty.propClass, pEaiProperty.propName, tStrValue, true));
        };
        this.mBasketId = pBasketId;
        this.mRecoveryBasketId = pBasketId;
        this.mMngr = pManager;
        this.mOapInteractors = new Interactors(this, this.mMngr);
        this.mArticleDataLock = new Lock();
        //register added/removed events
        this.eventAdded.addListener(() => this.mMngr.eventArticleAdded.trigger(this));
        this.eventRemoved.addListener(() => this.mMngr.eventArticleRemoved.trigger(this));
    }
    showInteractors(pShow) {
        this.mOapInteractors.showInteractors(pShow);
    }
    dispose() {
        super.dispose();
        this.mOapInteractors.dispose();
    }
    /**
     * Retrieves the article data for this article.
     * In order to retrieve the properties, the article has to be instantiated
     * on server side. This may not be possible if the ofml data is not installed or it has changed
     * (article requires migration). The operation would fail in those cases. If properties are not
     * required the noProperties option can be set to true to avoid this. (Default: false)
     */
    async getArticleData(pOptions) {
        await this.ensureIsInstanced();
        if (this.mBasketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        //getArticleData is called in multiple places, use lock to prevent concurrent calls
        const tLock = await this.mArticleDataLock.acquireLock();
        try {
            const tData = await this.getMainArticle()._getArticleData(this.mBasketId, pOptions);
            return (tData);
        }
        finally {
            tLock.unlock();
        }
    }
    getCachedArticleData(pOptions) {
        if (this.mBasketId == null)
            return (undefined);
        return (this.getMainArticle()._getCachedArticleData(this.mBasketId, pOptions));
    }
    getBasketItem() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (this.getMainArticle()._getBasketItem(this.mBasketId));
        });
        return (tPromise);
    }
    getCachedBasketItem() {
        if (this.mBasketId == null)
            return (undefined);
        return (this.getMainArticle()._getCachedBasketItem(this.mBasketId));
    }
    getItemProperties() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (this.getMainArticle()._getItemProperties(this.mBasketId));
        });
        return (tPromise);
    }
    getCachedItemProperties() {
        if (this.mBasketId == null)
            return (undefined);
        return (this.getMainArticle()._getCachedItemProperties(this.mBasketId));
    }
    getCalculation() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (this.getMainArticle()._getCalculation(this.mBasketId));
        });
        return (tPromise);
    }
    getCachedCalculation() {
        if (this.mBasketId == null)
            return (undefined);
        return (this.getMainArticle()._getCachedCalculation(this.mBasketId));
    }
    getCachedConfigData() {
        if (this.mBasketId == null)
            return (undefined);
        return (this.getMainArticle()._getCachedConfigData(this.mBasketId));
    }
    getCatalogImageUrl() {
        const tPromise = this.ensureIsInstanced().then(() => {
            if (this.mBasketId == null)
                return (rejectError(ERR_INVALID_STATE, "basketId is null"));
            return (this.getMainArticle()._getCatalogImageUrl(this.mBasketId));
        });
        return (tPromise);
    }
    getSubArticles(pDirectOnly) {
        const tSubArticles = new Array();
        this.collectSubArticles(this.mNode, tSubArticles, pDirectOnly);
        return (tSubArticles);
    }
    getSubArticleById(pBasketId) {
        return (this.findSubArticle(this.mNode, pBasketId));
    }
    async setEaiwsPropertyValue(pPropClass, pPropName, pValue, pUpdateGeometry) {
        await this.ensureIsInstanced();
        if (this.mBasketId == null)
            throw newError(ERR_INVALID_STATE, "basketId is null");
        let tOtherArticleChanged = false;
        try {
            this.mModel.beginModification();
            this.beginPropertyChange();
            const tOptions = new SetPropertyValueOptions();
            tOptions.computeVisibilityChangeFlags = false;
            tOptions.computeChoiceListChangeFlags = false;
            const tResult = await this.mMngr.session.basket.setPropertyValue(this.mBasketId, pPropClass, pPropName, pValue, tOptions);
            if (tResult != null && tResult !== "U") { //"U" means nothing changed
                const tMainArticle = this.getMainArticle();
                tOtherArticleChanged = tResult.indexOf("u") !== -1; //other item changed
                const tArticleRemoved = tResult.indexOf("r") !== -1; //item removed
                tMainArticle._clearCachedArticleInfo(this, tOtherArticleChanged);
                this.notifyChange(ElementChangedFlags.Properties);
                if (pUpdateGeometry)
                    await tMainArticle.updateGeometry(tArticleRemoved);
                this.mMngr._notifyArticleChanged(tOtherArticleChanged ? tMainArticle : this, tOtherArticleChanged);
                return (PropertyChangedResult.Properties);
            }
            else
                return (PropertyChangedResult.Nothing);
        }
        finally {
            //notify property change for whole composite
            if (tOtherArticleChanged) {
                const tMainArticle = this.getMainArticle();
                const tSubArticles = tMainArticle.getSubArticles(false);
                tMainArticle.notifyChange(ElementChangedFlags.Properties);
                for (const tSub of tSubArticles) {
                    tSub.notifyChange(ElementChangedFlags.Properties);
                }
            }
            this.endPropertyChange();
            this.mModel.endModification();
        }
    }
    getProperties() {
        /*prevent unknown item id exception
            We do this here to make it easier for the host application to handle cases where
            this article itself gets deleted after changing a property. This may be the case for sub-articles. */
        if (this.isDisposed)
            return (Promise.resolve(null));
        if (!this.mMngr.session.isValid)
            return (Promise.resolve(null));
        const tPromise = this.getArticleData().then((pData) => {
            return (ArticlePropertyHelpers.GetProperties(this.basketId, this.mMngr.session, this, pData, this.onSetPropertyValue));
        });
        return (tPromise);
    }
    getPropertyClasses() {
        /*prevent unknown item id exception
            We do this here to make it easier for the host application to handle cases where
            this article itself gets deleted after changing a property. This may be the case for sub-articles. */
        if (this.isDisposed)
            return (Promise.resolve(null));
        if (!this.mMngr.session.isValid)
            return (Promise.resolve(null));
        const tPromise = this.getArticleData().then((pData) => {
            return (ArticlePropertyHelpers.GetPropertyClasses(pData));
        });
        return (tPromise);
    }
    /**
     * Returns true if this article has interactors. E.g. OAP interactors.
     */
    async hasInteractors() {
        const tInteractors = await this.getMainArticle()._getOapInteractors(this);
        return (tInteractors != null && tInteractors.length > 0);
    }
    static GetEaiwsProperty(pProperty) {
        const tUserData = ArticlePropertyHelpers.GetPropertyUserData(pProperty);
        return (tUserData == null ? null : tUserData.eaiwsProperty);
    }
    //// internal //////////////////////////////////////////////////////////////////////////////
    /** @internal */
    _setBasketId(pBasketId) {
        this.mBasketId = pBasketId;
        this.mRecoveryBasketId = pBasketId;
        NodeUserData.SetData(this.mNode, ARTICLEELEMENT_NODEDATA_BASKETID, pBasketId);
    }
    /** @internal */
    _getRecoveryBasketId() {
        return (this.mRecoveryBasketId);
    }
    copyDataFrom(pElement) {
        super.copyDataFrom(pElement);
        if (pElement == null || !(pElement instanceof ArticleElement))
            return;
        this.mRecoveryBasketId = pElement.mRecoveryBasketId;
    }
    collectSubArticles(pParent, pArticles, pDirectOnly) {
        if (pParent == null)
            return;
        const tChilds = pParent.getChildTransformNodes(true);
        for (const tChild of tChilds) {
            let tCollectChilds = true;
            const tElement = SceneElement.FromNode(tChild, false);
            if (tElement != null && tElement instanceof ArticleElement && tElement.isSubArticle()) {
                pArticles.push(tElement);
                tCollectChilds = !pDirectOnly;
            }
            if (tCollectChilds)
                this.collectSubArticles(tChild, pArticles, pDirectOnly);
        }
    }
    findSubArticle(pParent, pBasketId) {
        if (pParent == null)
            return (null);
        let tFoundArticle = null;
        const tChilds = pParent.getChildTransformNodes(true);
        for (const tChild of tChilds) {
            const tElement = SceneElement.FromNode(tChild, false);
            if (tElement != null && tElement instanceof ArticleElement && tElement.isSubArticle() &&
                tElement.basketId === pBasketId) {
                tFoundArticle = tElement;
                break;
            }
            else {
                tFoundArticle = this.findSubArticle(tChild, pBasketId);
                if (tFoundArticle != null)
                    break;
            }
        }
        return (tFoundArticle);
    }
}
